// Extended self-service profile (Phase 2c) — bank accounts, dependents, a digital
// signature and a read-only employment-history timeline.
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, Trash2, Star, Upload, Banknote, Users, PenLine, Clock } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';
import { IMAGE_ACCEPT, downscaleSquareDataUrl } from './lib/image';

const RELATIONS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

export default function MyProfile({ showToast }) {
  const [d, setD] = useState(null);
  const [bank, setBank] = useState(null);
  const [dep, setDep] = useState(null);
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);

  const load = () => api.myProfile().then(setD).catch(() => setD({ bankAccounts: [], dependents: [], history: [], signatureUrl: null }));
  useEffect(() => { load(); }, []);

  const saveBank = async () => {
    if (!bank.bankName || !bank.accountNumber || busy) return; setBusy(true);
    try { await api.addBank(bank); showToast?.('Bank account added'); setBank(null); await load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const makePrimary = async (b) => { try { await api.updateBank(b.id, { isPrimary: true }); await load(); } catch (e) { showToast?.(e.message); } };
  const delBank = async (b) => { try { await api.deleteBank(b.id); await load(); } catch (e) { showToast?.(e.message); } };
  const saveDep = async () => {
    if (!dep.name || busy) return; setBusy(true);
    try { await api.addDependent(dep); showToast?.('Dependent added'); setDep(null); await load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const delDep = async (x) => { try { await api.deleteDependent(x.id); await load(); } catch (e) { showToast?.(e.message); } };
  const onSig = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    try { const dataUrl = await downscaleSquareDataUrl(f, 256); await api.setSignature(dataUrl); showToast?.('Signature saved'); await load(); }
    catch (err) { showToast?.(err.message); }
  };
  const clearSig = async () => { try { await api.clearSignature(); await load(); } catch (e) { showToast?.(e.message); } };

  if (!d) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <input ref={sigRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={onSig} />
      <SectionTitle title="My Profile" sub="Bank, dependents, signature and history" />

      {/* Bank accounts */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <div className="flex items-center gap-2"><Banknote size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Bank accounts</span></div>
          <OutlineBtn onClick={() => setBank({ bankName: '', accountNumber: '', iban: '', currency: 'PKR', isPrimary: false })}><Plus size={14} style={{ marginRight: 4 }} /> Add</OutlineBtn>
        </div>
        {d.bankAccounts.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No bank accounts yet.</p> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {d.bankAccounts.map((b) => (
              <div key={b.id} className="flex items-center justify-between" style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: '10px 12px' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{b.bankName} {b.isPrimary && <Pill tone="success">Primary</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{b.accountNumber}{b.iban ? ` · ${b.iban}` : ''} · {b.currency}</div>
                </div>
                <div className="flex items-center gap-1">
                  {!b.isPrimary && <button onClick={() => makePrimary(b)} title="Make primary" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Star size={15} /></button>}
                  <button onClick={() => delBank(b)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dependents */}
      <Card style={{ padding: 18 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <div className="flex items-center gap-2"><Users size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Dependents</span></div>
          <OutlineBtn onClick={() => setDep({ name: '', relation: 'Spouse', dob: '' })}><Plus size={14} style={{ marginRight: 4 }} /> Add</OutlineBtn>
        </div>
        {d.dependents.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No dependents added.</p> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {d.dependents.map((x) => (
              <div key={x.id} className="flex items-center justify-between" style={{ border: `1px solid ${AG.n200}`, borderRadius: 10, padding: '10px 12px' }}>
                <div><span style={{ fontWeight: 600, fontSize: 14, color: AG.text }}>{x.name}</span> <span style={{ fontSize: 12, color: AG.n500 }}>· {x.relation}{x.dob ? ` · ${x.dob}` : ''}</span></div>
                <button onClick={() => delDep(x)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Signature + History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card style={{ padding: 18 }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><PenLine size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Digital signature</span></div>
          {d.signatureUrl ? (
            <div className="flex items-center gap-3">
              <img src={d.signatureUrl} alt="signature" style={{ height: 56, borderRadius: 8, border: `1px solid ${AG.n200}`, background: '#fff' }} />
              <OutlineBtn onClick={() => sigRef.current?.click()}>Replace</OutlineBtn>
              <OutlineBtn onClick={clearSig}>Remove</OutlineBtn>
            </div>
          ) : (
            <OutlineBtn onClick={() => sigRef.current?.click()}><Upload size={14} style={{ marginRight: 6 }} /> Upload signature</OutlineBtn>
          )}
        </Card>
        <Card style={{ padding: 18 }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 12 }}><Clock size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Employment history</span></div>
          {d.history.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No history recorded yet.</p> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {d.history.map((h) => (
                <div key={h.id} className="flex items-center gap-2">
                  <Pill tone={h.kind === 'Promotion' ? 'success' : h.kind === 'SalaryChange' ? 'info' : 'neutral'}>{h.kind}</Pill>
                  <span style={{ fontSize: 13, color: AG.text }}>{h.title}</span>
                  <span style={{ fontSize: 12, color: AG.n400 }}>· {h.effective}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!bank} onClose={() => !busy && setBank(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add bank account</DialogTitle>
        <DialogContent>
          {bank && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Bank name" value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Account number" value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} sx={{ mb: 1.5 }} />
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" label="IBAN (optional)" value={bank.iban} onChange={(e) => setBank({ ...bank, iban: e.target.value })} />
                <TextField size="small" label="Currency" value={bank.currency} onChange={(e) => setBank({ ...bank, currency: e.target.value })} />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setBank(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={saveBank} disabled={busy}>Add</PrimaryBtn></DialogActions>
      </Dialog>

      <Dialog open={!!dep} onClose={() => !busy && setDep(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add dependent</DialogTitle>
        <DialogContent>
          {dep && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Name" value={dep.name} onChange={(e) => setDep({ ...dep, name: e.target.value })} sx={{ mb: 1.5 }} />
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Relation</label>
              <select value={dep.relation} onChange={(e) => setDep({ ...dep, relation: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>{RELATIONS.map((r) => <option key={r}>{r}</option>)}</select>
              <TextField fullWidth size="small" type="date" label="Date of birth (optional)" InputLabelProps={{ shrink: true }} value={dep.dob} onChange={(e) => setDep({ ...dep, dob: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setDep(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={saveDep} disabled={busy}>Add</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
